import { between, maxLength, numeric, required, requiredIf } from 'vuelidate/lib/validators';
import { isNoEmptyField } from '@/assets/validators/validatorHelpers';
import beamlineSpecification from '@/json/beamlineSpecification';
import { notationNumberRegex } from '@/assets/validators/regexValidator';

const endStationSpecification = beamlineSpecification.endStationASpecification;

const endStationValidator = {
  endStationParameters: {
    photon_energy_from: {
      required,
      numeric,
      isSmallerThan() {
        return parseInt(this.endStationParameters.photon_energy_from, 10)
          <= parseInt(this.endStationParameters.photon_energy_to, 10);
      },
      between: between(
        endStationSpecification.energy_range.energy_range_from,
        endStationSpecification.energy_range.energy_range_to,
      ),
    },
    photon_energy_to: {
      required,
      numeric,
      isGreaterThan() {
        return parseInt(this.endStationParameters.photon_energy_to, 10)
          >= parseInt(this.endStationParameters.photon_energy_from, 10);
      },
      between: between(
        endStationSpecification.energy_range.energy_range_from,
        endStationSpecification.energy_range.energy_range_to,
      ),
    },
    photon_polarisation: { required },
    detection_modes: { required },
    measurement_temperature_from: {
      required,
      numeric,
      isSmallerThan() {
        return parseInt(this.endStationParameters.measurement_temperature_from, 10)
          <= parseInt(this.endStationParameters.measurement_temperature_to, 10);
      },
      between: between(
        endStationSpecification.temperature_range.temperature_range_from,
        endStationSpecification.temperature_range.temperature_range_to,
      ),
    },
    measurement_temperature_to: {
      required,
      numeric,
      isGreaterThan() {
        return parseInt(this.endStationParameters.measurement_temperature_to, 10)
          >= parseInt(this.endStationParameters.measurement_temperature_from, 10);
      },
      between: between(
        endStationSpecification.temperature_range.temperature_range_from,
        endStationSpecification.temperature_range.temperature_range_to,
      ),
    },
    cryogenic_liquids: { required },
    lhe_use_justification: {
      requiredIf: requiredIf(function isLHeSelected() {
        if (this.endStationParameters.cryogenic_liquids) {
          return this.endStationParameters.cryogenic_liquids.includes('LHe');
        }
        return false;
      }),
      maxLength: maxLength(300),
    },
    preparation_in_situ: { required },
    preparation_temperature_from: {
      requiredIf: requiredIf((val) => val.preparation_in_situ),
      numeric,
      isSmallerThan() {
        if (!this.endStationParameters.preparation_in_situ) {
          return true;
        }
        return parseInt(this.endStationParameters.preparation_temperature_from, 10)
          <= parseInt(this.endStationParameters.preparation_temperature_to, 10);
      },
      between: between(
        endStationSpecification.sample_temperature_in_situ.temperature_range_from,
        endStationSpecification.sample_temperature_in_situ.temperature_range_to,
      ),
    },
    preparation_temperature_to: {
      requiredIf: requiredIf((val) => val.preparation_in_situ),
      numeric,
      isGreaterThan() {
        if (!this.endStationParameters.preparation_in_situ) {
          return true;
        }
        return parseInt(this.endStationParameters.preparation_temperature_to, 10)
          >= parseInt(this.endStationParameters.preparation_temperature_from, 10);
      },
      between: between(
        endStationSpecification.sample_temperature_in_situ.temperature_range_from,
        endStationSpecification.sample_temperature_in_situ.temperature_range_to,
      ),
    },
    evaporation: { required },
    evaporation_data: {
      requiredIf(val) {
        if (this.endStationParameters.evaporation) {
          return isNoEmptyField(val);
        }
        return true;
      },
    },
    ar_sputtering: { required },
    crystal_clever: { required },
    reaction_chamber: { required },
    reaction_chamber_data: {
      requiredIf: requiredIf((val) => val.reaction_chamber),
      notEmptyField(val) {
        if (this.endStationParameters.reaction_chamber && val) {
          return isNoEmptyField(val.reduce((acc, el) => acc.concat([Object.values(el)]), []));
        }
        return true;
      },
      regexValidation(val) {
        if (this.endStationParameters.reaction_chamber && val) {
          const gasPressures = val.reduce((acc, el) => acc.concat([Object.values(el)]), [])
            .map((el) => el[1]);
          return gasPressures.every((el) => notationNumberRegex.regex.test(el));
        }
        return true;
      },
      rangeValidation(val) {
        if (this.endStationParameters.reaction_chamber && val) {
          const gasPressures = val.reduce((acc, el) => acc.concat([Object.values(el)]), [])
            .map((el) => el[1]);
          if (!gasPressures.every((el) => {
            el = Number(el);
            return el <= endStationSpecification.reaction_chamber_gas_pressure.to && el
              >= endStationSpecification.reaction_chamber_gas_pressure.from;
          })) {
            return false;
          }

          const temperature = val.reduce((acc, el) => acc.concat([Object.values(el)]), [])
            .map((el) => el[2]);
          if (!temperature.every((el) => el <= endStationSpecification.reaction_chamber_temperature.to && el
            >= endStationSpecification.reaction_chamber_temperature.from)) {
            return false;
          }
        }
        return true;
      },
    },
  },
};
export { endStationValidator };
